import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, RotateCw, X } from "lucide-react";
import { useClickOutside } from "hooks/useClickOutside";
import { GiveawayUserOption } from "types/giveaway";
import { searchWinnerCandidates } from "apis/adminServices/giveawayServices";

export interface WinnerChipItem {
  winnerId?: number;
  userId: number;
  userName: string;
  email?: string;
  profilePictureUrl?: string;
  status?: string; // "Active" | "CreditFailed"
  creditError?: string | null;
}

interface WinnerUserPickerProps {
  selectedUsers: WinnerChipItem[];
  onAddUser: (user: GiveawayUserOption) => void;
  onRemoveUser: (userId: number) => void;
  onRetryCredit?: (winnerId: number) => void;
  retryingWinnerIds?: number[];
  excludeUserIds?: number[];
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  /**
   * 1st/2nd/3rd Prize each take exactly one winner — once one is selected, the
   * search input hides (the X on the chip still works) instead of letting the
   * admin pick a second, which would silently replace the first.
   */
  singleSelect?: boolean;
  /**
   * Additional Winners: once the configured cap is reached, hide the search
   * input entirely (only the chips' remove buttons stay available) instead of
   * letting the admin attempt another search that can never succeed.
   */
  capReached?: boolean;
  /** Called when the user clicks/interacts outside this picker — used to
   * dismiss a transient "cap reached" warning so it doesn't linger. */
  onDismissWarning?: () => void;
}

/**
 * User picker for the winner announcement screen. Shows selected users as
 * removable chips (with special CreditFailed warning styling); allows
 * searching the user list and adding candidates — one for a prize-tier slot
 * (`singleSelect`), several for Additional Winners.
 */
const WinnerUserPicker: React.FC<WinnerUserPickerProps> = ({
  selectedUsers,
  onAddUser,
  onRemoveUser,
  onRetryCredit,
  retryingWinnerIds = [],
  excludeUserIds = [],
  disabled = false,
  error,
  placeholder = "Search a user by name or email...",
  singleSelect = false,
  capReached = false,
  onDismissWarning,
}) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<GiveawayUserOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => setOpen(false), open);
  // Dismiss a lingering "cap reached" warning as soon as the admin clicks
  // anywhere else — it should only reappear on the next add attempt.
  useClickOutside(containerRef, () => onDismissWarning?.(), !!error && !!onDismissWarning);

  // Debounced search — 300ms
  useEffect(() => {
    if (!open || disabled || capReached || (singleSelect && selectedUsers.length > 0)) return;

    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      searchWinnerCandidates(query)
        .then((users) => {
          if (!active) return;
          // Filter out users who are already selected or in exclude list
          const existingIds = new Set([
            ...selectedUsers.map((u) => u.userId),
            ...excludeUserIds,
          ]);
          setOptions(users.filter((u) => !existingIds.has(u.userId)));
        })
        .catch((err) => {
          console.error("Failed to search users:", err);
          if (active) setOptions([]);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, open, selectedUsers, excludeUserIds, disabled, singleSelect, capReached]);

  const handleSelect = (user: GiveawayUserOption) => {
    onAddUser(user);
    setQuery("");
    setOpen(false);
  };

  // A prize-tier slot takes exactly one winner — once selected, hide the
  // search input (only the chip's remove button stays available) instead of
  // letting the admin pick a second one, which would silently replace it.
  const canAddMore =
    !disabled && (!singleSelect || selectedUsers.length === 0) && !capReached;

  // Once the cap is hit (or a slot fills up), collapse the search UI instead
  // of leaving a dead input/dropdown open.
  useEffect(() => {
    if (!canAddMore) {
      setOpen(false);
      setQuery("");
    }
  }, [canAddMore]);

  return (
    <div className="space-y-3" ref={containerRef}>
      {/* Selected Users Chips Container */}
      <div
        className={`w-full min-h-[48px] p-2.5 rounded-lg border bg-white flex items-center flex-wrap gap-2 transition-colors ${
          error ? "border-red-500" : "border-[#D1D5DB]"
        } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
      >
        {selectedUsers.map((user) => {
          const isCreditFailed = user.status === "CreditFailed";
          const isRetrying = user.winnerId
            ? retryingWinnerIds.includes(user.winnerId)
            : false;

          return (
            <span
              key={user.userId}
              className={`inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-md text-sm font-medium transition-all ${
                isCreditFailed
                  ? "bg-red-50 border border-red-200 text-red-700"
                  : "bg-gray-100 border border-gray-200 text-grease-100"
              }`}
            >
              <span>{user.userName}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemoveUser(user.userId)}
                  aria-label={`Remove ${user.userName}`}
                  className="text-lightgray-200 hover:text-grease-100 p-0.5 rounded transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          );
        })}

        {canAddMore && (
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={selectedUsers.length ? "Add another winner..." : placeholder}
            autoComplete="off"
            className="flex-1 min-w-[180px] border-0 p-1 text-sm text-grease-100 placeholder-gray-400 focus:ring-0 focus:outline-none bg-transparent"
          />
        )}
      </div>

      {/* CreditFailed Warning Banners */}
      {selectedUsers.some((u) => u.status === "CreditFailed") && (
        <div className="space-y-2">
          {selectedUsers
            .filter((u) => u.status === "CreditFailed")
            .map((failedUser) => {
              const isRetrying = failedUser.winnerId
                ? retryingWinnerIds.includes(failedUser.winnerId)
                : false;

              return (
                <div
                  key={`failed-${failedUser.userId}`}
                  className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center justify-between gap-3 text-xs text-red-700"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                    <span className="truncate">
                      Token could not be credited to <strong>{failedUser.userName}</strong>. Please retry from Winner Management.
                    </span>
                  </div>
                  {failedUser.winnerId && onRetryCredit && (
                    <button
                      type="button"
                      onClick={() => onRetryCredit(failedUser.winnerId!)}
                      disabled={isRetrying}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-semibold shrink-0 inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
                    >
                      <RotateCw className={`w-3 h-3 ${isRetrying ? "animate-spin" : ""}`} />
                      <span>{isRetrying ? "Retrying..." : "Retry"}</span>
                    </button>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Autocomplete dropdown options */}
      {open && canAddMore && (
        <div className="relative">
          <div className="absolute z-30 w-full mt-1 bg-white rounded-lg shadow-xl border border-gray-100 max-h-60 overflow-auto">
            {loading ? (
              <p className="p-4 text-sm text-lightgray-200 text-center">
                Searching candidates...
              </p>
            ) : options.length ? (
              <ul>
                {options.map((user) => (
                  <li key={user.userId}>
                    <button
                      type="button"
                      onClick={() => handleSelect(user)}
                      className="w-full text-left px-4 py-2.5 hover:bg-yellow-50 border-b border-gray-100 last:border-0 transition-colors"
                    >
                      <span className="block text-sm font-medium text-grease-100">
                        {user.userName}
                      </span>
                      {user.email && (
                        <span className="block text-xs text-lightgray-200">
                          {user.email}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-4 text-sm text-lightgray-200 text-center">
                {query.trim() ? "No users match your search" : "Type a name or email to search users"}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WinnerUserPicker;
